// src/components/VotacionCard.js 
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, setDoc, serverTimestamp, writeBatch, query, where } from 'firebase/firestore';
import { db } from '../firebase';
// import { doc, runTransaction, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { FaCheck, FaTimes, FaEdit, FaTrash, FaEye, FaCheckCircle, FaUsers, FaShieldAlt, FaCoins, FaBuilding, FaChevronDown, FaChevronUp, FaHome, FaCommentDots } from 'react-icons/fa';
import '../pages/Votaciones.css';
import { registerMovement } from '../utils/registerMovement';
import { obtenerNombreResidente } from '../utils/morosoUtils';
import {
    calcularEstadoVotacionEfectivo,
    getParticipationTarget,
    normalizarFechaFinVotacion,
    esResidenteHabilitadoParaVotacion,
} from '../utils/votacionesUtils';
import ConfirmDialog from './ConfirmDialog';

const VotacionCard = ({ votacion, isAdmin = false, onDeleted, totalParticipantes: totalParticipantesProp = 0 }) => {
    const { userData, currentUser, casaActiva, misCasas } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const [selectedOption, setSelectedOption] = useState(null);
    const [haVotado, setHaVotado] = useState(false);
    const [opciones, setOpciones] = useState(votacion.opciones || {});
    const [showDetails, setShowDetails] = useState(false);
    const [ackInfo, setAckInfo] = useState(null);
    const [ackLoading, setAckLoading] = useState(false);
    const [ackSaving, setAckSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCasasDropdown, setShowCasasDropdown] = useState(false);
    const [casasDetalle, setCasasDetalle] = useState([]);
    const [loadingCasasDetalle, setLoadingCasasDetalle] = useState(false);
    const [opinionContra, setOpinionContra] = useState('');
    const [opinionesList, setOpinionesList] = useState([]);
    const [loadingOpiniones, setLoadingOpiniones] = useState(false);

    const esAdminUsuario = String(userData?.rol || '').toLowerCase() === 'superadmin';
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const uid = userData?.uid || currentUser?.uid || null;
    const esPersonalizada = (Array.isArray(votacion.casasHabilitadas) && votacion.casasHabilitadas.length > 0) || (Array.isArray(votacion.numerosCasasHabilitadas) && votacion.numerosCasasHabilitadas.length > 0);
    const participationTarget = getParticipationTarget(totalParticipantesProp, votacion);
    const totalVotos = Object.values(opciones).reduce((a, b) => a + b, 0);
    const denominator = participationTarget > 0 ? participationTarget : Math.max(totalVotos, 1);
    const calcularPorcentaje = (votos) => {
        if (denominator === 0) return 0;
        return Math.min(100, Math.round((votos / denominator) * 100));
    };
    const participationPercent = participationTarget > 0
        ? Math.min(100, Math.round((totalVotos / participationTarget) * 100))
        : null;
    const participationLabel = participationTarget > 0
        ? `${totalVotos}/${participationTarget} ${esPersonalizada ? 'domicilios' : 'residentes'}`
        : `${totalVotos} voto${totalVotos === 1 ? '' : 's'}`;
    const fechaCreacionLabel = formatDateShort(votacion.fechaCreacion);
    const fechaFinLabel = formatDateShort(normalizarFechaFinVotacion(votacion.fechaFin));
    const acknowledgedCount = Number(ackInfo?.conteoTotal) || 0;
    const needsReview = isAdmin && totalVotos > 0 && totalVotos > acknowledgedCount;

    useEffect(() => {
        setOpciones(votacion.opciones || {});
    }, [votacion.opciones]);

    useEffect(() => {
        if (!showCasasDropdown || !isAdmin || !fraccionamientoId || !esPersonalizada) return;
        const cIds = votacion.casasHabilitadas || [];
        const numIds = votacion.numerosCasasHabilitadas || [];

        let cancelled = false;
        const cargarDetalles = async () => {
            setLoadingCasasDetalle(true);
            try {
                const [casasSnap, usersSnap] = await Promise.all([
                    getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'casas')),
                    getDocs(query(collection(db, 'usuarios'), where('fraccionamientoId', '==', fraccionamientoId)))
                ]);
                if (cancelled) return;

                const usersMap = {};
                usersSnap.docs.forEach(d => {
                    const u = d.data() || {};
                    const name = (u.nombreCompleto || u.nombre || `${u.nombres || ''} ${u.apellidos || ''}`).trim();
                    if (name) {
                        if (u.casaId) usersMap[u.casaId] = name;
                        if (u.numeroCasa) usersMap[String(u.numeroCasa).trim()] = name;
                    }
                });

                const cSet = new Set(cIds.map(String));
                const numSet = new Set(numIds.map(String));
                const lista = [];

                casasSnap.docs.forEach(d => {
                    const c = { id: d.id, ...d.data() };
                    const num = String(c.numero || c.numeroCasa || '').trim();
                    if (cSet.has(d.id) || (num && numSet.has(num))) {
                        const nombre = obtenerNombreResidente(c) || usersMap[d.id] || usersMap[num] || '';
                        lista.push({
                            id: d.id,
                            numero: num || d.id,
                            calle: c.calle || '',
                            residente: nombre
                        });
                    }
                });

                numIds.forEach(num => {
                    const strNum = String(num).trim();
                    if (!lista.some(item => String(item.numero) === strNum)) {
                        lista.push({
                            id: strNum,
                            numero: strNum,
                            calle: '',
                            residente: usersMap[strNum] || ''
                        });
                    }
                });

                lista.sort((a, b) => {
                    const nA = parseInt(a.numero, 10);
                    const nB = parseInt(b.numero, 10);
                    if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
                    return String(a.numero).localeCompare(String(b.numero));
                });

                setCasasDetalle(lista);
            } catch (err) {
                console.warn('VotacionCard: error cargando domicilios habilitados', err);
            } finally {
                if (!cancelled) setLoadingCasasDetalle(false);
            }
        };

        cargarDetalles();
        return () => { cancelled = true; };
    }, [showCasasDropdown, isAdmin, fraccionamientoId, esPersonalizada, votacion.casasHabilitadas, votacion.numerosCasasHabilitadas]);

    const computedStatus = calcularEstadoVotacionEfectivo(votacion);
    const isExpired = computedStatus === 'vencida';
    const isUpcoming = computedStatus === 'programada';
    const estaHabilitado = esResidenteHabilitadoParaVotacion(votacion, userData, casaActiva, misCasas);
    const puedeInteractuar = !haVotado && !isExpired && !isUpcoming && !esAdminUsuario && estaHabilitado;

    useEffect(() => {
        // ESTA ES LA GUARDIA:
        // Si userData o userData.uid todavía no existen, no hagas nada.
        // El hook se volverá a ejecutar cuando userData se actualice.
        if (!uid || !fraccionamientoId) {
            return;
        }

        const verificarVoto = async () => {
            // Ahora que estamos aquí, estamos seguros de que todos los datos necesarios existen.
            const votanteRef = doc(db, `fraccionamientos/${fraccionamientoId}/votaciones/${votacion.id}/votantes`, uid);
            const docSnap = await getDoc(votanteRef);
            if (docSnap.exists()) {
                setHaVotado(true);
                const data = docSnap.data();
                if (data?.opcion) {
                    setSelectedOption(data.opcion);
                }
                if (data?.opinion) {
                    setOpinionContra(data.opinion);
                }
            }
        };
        verificarVoto();

        // La dependencia en 'userData' es la clave.
    }, [votacion.id, uid, fraccionamientoId]);

    useEffect(() => {
        if (!isAdmin || !uid || !fraccionamientoId || !votacion?.id) {
            setAckInfo(null);
            return;
        }

        let cancelled = false;

        const fetchAck = async () => {
            try {
                setAckLoading(true);
                const ackRef = doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', votacion.id, 'lecturasAdmin', uid);
                const ackSnap = await getDoc(ackRef);
                if (!cancelled) {
                    setAckInfo(ackSnap.exists() ? ackSnap.data() : null);
                }
            } catch (error) {
                console.error('VotacionCard: error obteniendo lectura administrativa', error);
                if (!cancelled) {
                    setAckInfo(null);
                }
            } finally {
                if (!cancelled) {
                    setAckLoading(false);
                }
            }
        };

        fetchAck();

        return () => {
            cancelled = true;
        };
    }, [isAdmin, uid, fraccionamientoId, votacion?.id]);

    useEffect(() => {
        if (!showDetails || !isAdmin || !fraccionamientoId || !votacion?.id) {
            return;
        }

        let cancelled = false;
        const cargarOpiniones = async () => {
            setLoadingOpiniones(true);
            try {
                const snap = await getDocs(
                    collection(db, 'fraccionamientos', fraccionamientoId, 'votaciones', votacion.id, 'votantes')
                );
                if (cancelled) return;
                const lista = [];
                snap.docs.forEach((d) => {
                    const data = d.data() || {};
                    if (data.opinion && typeof data.opinion === 'string' && data.opinion.trim()) {
                        lista.push({
                            id: d.id,
                            ...data
                        });
                    }
                });
                lista.sort((a, b) => {
                    const tA = a.votadoEn?.seconds || (a.votadoEn?.toDate ? a.votadoEn.toDate().getTime() : 0);
                    const tB = b.votadoEn?.seconds || (b.votadoEn?.toDate ? b.votadoEn.toDate().getTime() : 0);
                    return tB - tA;
                });
                setOpinionesList(lista);
            } catch (err) {
                console.warn('VotacionCard: error al cargar opiniones administrativas', err);
            } finally {
                if (!cancelled) {
                    setLoadingOpiniones(false);
                }
            }
        };

        cargarOpiniones();
        return () => {
            cancelled = true;
        };
    }, [showDetails, isAdmin, fraccionamientoId, votacion?.id]);

    const handleVotar = async () => {
        const optionToVote = selectedOption;

        if (!optionToVote) {
            showNotification('Por favor, selecciona una opción.', 'error');
            return;
        }

        if (isExpired) {
            showNotification('Esta votación ya está vencida.', 'info');
            return;
        }

        if (isUpcoming) {
            showNotification('La votación aún no inicia.', 'info');
            return;
        }

        // Si el usuario es admin, le informamos que no puede votar
        if (esAdminUsuario) {
            showNotification('Los administradores no pueden participar en las votaciones.', 'info');
            return;
        }

        if (!fraccionamientoId) {
            showNotification('No se pudo determinar tu fraccionamiento.', 'error');
            return;
        }

        if (!uid) {
            showNotification('No se pudo identificar tu usuario.', 'error');
            return;
        }

        const votacionRef = doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', votacion.id);
        const votanteRef = doc(votacionRef, 'votantes', uid);

        try {
            // Verificamos primero si ya votó para no hacer una escritura innecesaria
            const votanteDoc = await getDoc(votanteRef);
            if (votanteDoc.exists()) {
                showNotification('Ya has votado en esta propuesta.', 'error');
                return;
            }

            const actorNombre = [
                userData?.nombreCompleto,
                userData?.nombre,
                currentUser?.displayName,
                currentUser?.email,
            ].find((value) => typeof value === 'string' && value.trim().length > 0) || currentUser?.uid || uid;

            const userCasa = String(casaActiva?.numero || casaActiva?.numeroCasa || userData?.numeroCasa || userData?.casa || '').trim() || 'Sin número';

            const votoPayload = {
                opcion: optionToVote,
                votadoEn: serverTimestamp()
            };

            if ((optionToVote === 'enContra' || optionToVote === 'contra') && opinionContra.trim()) {
                votoPayload.opinion = opinionContra.trim();
                votoPayload.casa = userCasa;
                votoPayload.residente = actorNombre;
            }

            // El usuario solo escribe un documento simple en la subcolección 'votantes'
            // La Cloud Function se encargará del resto
            await setDoc(votanteRef, votoPayload);

            setSelectedOption(optionToVote);
            setHaVotado(true);
            setOpciones((prev) => ({
                ...prev,
                [optionToVote]: (prev && typeof prev[optionToVote] === 'number' ? prev[optionToVote] : 0) + 1,
            }));
            showNotification('¡Gracias por tu voto! Los resultados se actualizarán en breve.', 'success');

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: 'Participación en votación',
                    descripcion: `${actorNombre} participó en "${votacion.titulo}".`,
                    categoria: 'Votaciones',
                    tipoMovimiento: 'votacion_participacion',
                    origen: 'votaciones',
                    metadata: {
                        votacionId: votacion.id,
                        opcion: optionToVote,
                        opinion: (optionToVote === 'enContra' || optionToVote === 'contra') && opinionContra.trim() ? opinionContra.trim() : null,
                        actorUid: uid,
                        actorEmail: currentUser?.email || null,
                    },
                });
            } catch (logError) {
                console.error('VotacionCard: no se pudo registrar la participación en notificaciones', logError);
            }

        } catch (error) {
            console.error("Error al registrar el voto: ", error);
            showNotification('Hubo un error al registrar tu voto.', 'error');
        }
    };

    const handleEdit = () => {
        navigate(`/comunidad/votaciones/editar/${votacion.id}`);
    };

    const handleDelete = () => {
        if (!isAdmin) return;
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!isAdmin) {
            setShowDeleteConfirm(false);
            return;
        }
        try {
            if (!fraccionamientoId) {
                showNotification('No se pudo determinar tu fraccionamiento.', 'error');
                setShowDeleteConfirm(false);
                return;
            }

            const votacionRef = doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', votacion.id);
            const votantesRef = collection(votacionRef, 'votantes');
            const votantesSnap = await getDocs(votantesRef);

            // Eliminamos la votación y los votos asociados en una sola transacción por confiabilidad.
            const batch = writeBatch(db);
            votantesSnap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
            batch.delete(votacionRef);
            await batch.commit();
            showNotification('Votación eliminada correctamente.', 'success');
            if (onDeleted) {
                onDeleted(votacion.id);
            }
        } catch (error) {
            console.error('Error al eliminar la votación:', error);
            showNotification('No se pudo eliminar la votación. Intenta nuevamente.', 'error');
        }
        setShowDeleteConfirm(false);
    };

    const handleMarkReviewed = async () => {
        if (!isAdmin || !fraccionamientoId || !uid) {
            return;
        }

        if (totalVotos <= 0) {
            showNotification('Aún no hay votos registrados para esta votación.', 'info');
            return;
        }

        try {
            setAckSaving(true);
            const ackRef = doc(db, 'fraccionamientos', fraccionamientoId, 'votaciones', votacion.id, 'lecturasAdmin', uid);
            await setDoc(ackRef, {
                conteoTotal: totalVotos,
                revisadoEn: serverTimestamp(),
            }, { merge: true });
            setAckInfo({ conteoTotal: totalVotos, revisadoEn: new Date() });
            showNotification('Marcaste los votos como revisados.', 'success');
        } catch (error) {
            console.error('VotacionCard: error al registrar la revisión de votos', error);
            showNotification('No se pudo marcar la revisión. Intenta nuevamente.', 'error');
        } finally {
            setAckSaving(false);
        }
    };

    const OPCIONES_CONFIG = {
        aFavor: { texto: "Voto a favor", icono: <FaCheck />, color: "green" },
        enContra: { texto: "Voto en contra", icono: <FaTimes />, color: "red" },
        // 'masInfo' option removed per UI change
    };

    const obtenerInfoCategoria = (cat = 'Comunidad') => {
        switch (cat) {
            case 'Vigilancia/Seguridad':
                return { label: 'Vigilancia / Seguridad', icono: <FaShieldAlt />, className: 'cat-vigilancia' };
            case 'Finanzas (Ingresos/Egresos)':
                return { label: 'Finanzas', icono: <FaCoins />, className: 'cat-finanzas' };
            case 'Administración':
                return { label: 'Administración', icono: <FaBuilding />, className: 'cat-administracion' };
            case 'Comunidad':
            default:
                return { label: 'Comunidad', icono: <FaUsers />, className: 'cat-comunidad' };
        }
    };

    return (
        <div className={`votacion-card ${haVotado ? 'votado' : ''}`}>
            <div className="card-header">
                <div className="votacion-title-wrapper">
                    <h3>{votacion.titulo}</h3>
                    {(() => {
                        const catInfo = obtenerInfoCategoria(votacion.categoria || 'Comunidad');
                        return (
                            <span className={`categoria-badge ${catInfo.className}`}>
                                {catInfo.icono}
                                <span>{catInfo.label}</span>
                            </span>
                        );
                    })()}
                </div>
                {(() => {
                    const badge = computeStatusBadge(computedStatus);
                    return badge ? <span className={`status-badge ${badge.className}`}>{badge.label}</span> : null;
                })()}
            </div>
            <div className="card-meta">
                <span>Creada: {fechaCreacionLabel || '—'}</span>
                <span>Vence: {fechaFinLabel || '—'}</span>
                {participationTarget > 0 && (
                    esPersonalizada && isAdmin ? (
                        <button
                            type="button"
                            className={`meta-pill-btn ${showCasasDropdown ? 'active' : ''}`}
                            onClick={() => setShowCasasDropdown(!showCasasDropdown)}
                            title="Hacer clic para ver los domicilios habilitados en esta votación"
                        >
                            <span>Domicilios habilitados: {participationTarget}</span>
                            <span className="pill-arrow">{showCasasDropdown ? <FaChevronUp /> : <FaChevronDown />}</span>
                        </button>
                    ) : (
                        <span>
                            {esPersonalizada
                                ? `Domicilios habilitados: ${participationTarget}`
                                : `Residentes habilitados: ${participationTarget}`}
                        </span>
                    )
                )}
            </div>

            {isAdmin && esPersonalizada && showCasasDropdown && (
                <div className="votacion-casas-dropdown">
                    <div className="casas-dropdown-header">
                        <span className="casas-dropdown-title">
                            <FaHome /> Domicilios participantes ({participationTarget}):
                        </span>
                        <button
                            type="button"
                            className="casas-dropdown-close"
                            onClick={() => setShowCasasDropdown(false)}
                            title="Cerrar lista de domicilios"
                        >
                            ✕
                        </button>
                    </div>
                    {loadingCasasDetalle && casasDetalle.length === 0 ? (
                        <p className="casas-dropdown-loading">Cargando domicilios...</p>
                    ) : (
                        <div className="casas-dropdown-grid">
                            {(casasDetalle.length > 0 ? casasDetalle : (votacion.numerosCasasHabilitadas || []).map(num => ({ numero: num, residente: '' }))).map((c, idx) => (
                                <div key={c.id || idx} className="casas-dropdown-chip">
                                    <span className="chip-house">Casa {c.numero}</span>
                                    {c.residente && <span className="chip-resident">· {c.residente}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* fechas y descripción se muestran ahora en el panel de detalles */}
            <div className="options-container">
                {Object.keys(OPCIONES_CONFIG).map(key => (
                    <div
                        key={key}
                        className={`votacion-option ${selectedOption === key ? 'selected' : ''} ${!puedeInteractuar ? 'disabled' : ''}`}
                        onClick={() => puedeInteractuar && setSelectedOption(key)}
                    >
                        <div className="option-label">
                            {OPCIONES_CONFIG[key].icono}
                            <span>{OPCIONES_CONFIG[key].texto}</span>
                        </div>
                        <div className="option-result">
                            <div className="progress-bar-container">
                                <div
                                    className={`progress-bar ${OPCIONES_CONFIG[key].color}`}
                                    style={{ width: `${calcularPorcentaje(opciones[key] || 0)}%` }}
                                ></div>
                            </div>
                            <span className="percentage">{calcularPorcentaje(opciones[key] || 0)}%</span>
                        </div>
                    </div>
                ))}
            </div>
            {(selectedOption === 'enContra' || selectedOption === 'contra') && puedeInteractuar && !haVotado && (
                <div className="votacion-opinion-box">
                    <label className="votacion-opinion-label">
                        <FaCommentDots className="opinion-icon" />
                        <span>Motivo o sugerencia de tu voto en contra (Opcional):</span>
                    </label>
                    <textarea
                        className="votacion-opinion-textarea"
                        placeholder="Explica brevemente por qué consideras que debe denegarse o qué alternativa propones a la administración..."
                        value={opinionContra}
                        onChange={(e) => setOpinionContra(e.target.value.slice(0, 300))}
                        rows={3}
                    />
                    <div className="votacion-opinion-counter">
                        {opinionContra.length}/300 caracteres
                    </div>
                </div>
            )}
            <div className="participation-summary">
                <span>{participationLabel}</span>
                <span>{participationPercent !== null ? `${participationPercent}% de participación` : 'Participación registrada'}</span>
            </div>
            {isAdmin && totalVotos > 0 && (
                <div className={`admin-review-status ${needsReview ? 'pending' : 'done'}`}>
                    {needsReview ? 'Pendiente de revisión' : 'Votos revisados'}
                </div>
            )}
            <div className="card-actions">
                {isAdmin && (
                    <div className="admin-actions">
                        <button
                            className="btn-secondary"
                            onClick={handleMarkReviewed}
                            disabled={ackLoading || ackSaving || !needsReview}
                        >
                            <FaCheckCircle />
                            {ackSaving ? 'Guardando...' : ackLoading ? 'Cargando...' : (needsReview ? 'Marcar como revisado' : 'Revisado')}
                        </button>
                        <button className="btn-secondary" onClick={handleEdit}>
                            <FaEdit /> Editar
                        </button>
                        <button className="btn-danger" onClick={handleDelete}>
                            <FaTrash /> Eliminar
                        </button>
                    </div>
                )}
                <div className="action-right">
                    <button className="btn-secondary" onClick={() => setShowDetails(!showDetails)}>
                        <FaEye /> {showDetails ? 'Ocultar Detalles' : 'Ver Detalles'}
                    </button>
                    {!esAdminUsuario && (
                        <button
                            className={haVotado ? "btn-voted" : "btn-primary"}
                            onClick={handleVotar}
                            disabled={haVotado || !selectedOption || !puedeInteractuar}
                        >
                            {haVotado ? <FaCheckCircle /> : <FaCheck />}
                            {haVotado ? 'Ya Votaste' : 'Confirmar Voto'}
                        </button>
                    )}
                </div>
            </div>

            {showDetails && (
                <div className="votacion-details-panel">
                    <div className="details-row"><strong>Creada:</strong> {votacion.fechaCreacion ? formatDate(votacion.fechaCreacion) : '—'}</div>
                    <div className="details-row"><strong>Vence:</strong> {votacion.fechaFin ? formatDate(votacion.fechaFin) : '—'}</div>
                    <div className="details-description">{votacion.descripcion}</div>
                    {isAdmin && ackInfo?.revisadoEn && (
                        <div className="details-row"><strong>Revisado por ti:</strong> {formatDate(ackInfo.revisadoEn)}</div>
                    )}
                    {isAdmin && (
                        <div className="details-opiniones-section">
                            <div className="opiniones-section-title">
                                <FaCommentDots />
                                <span>Opiniones y Sugerencias (Votos en contra) ({opinionesList.length}):</span>
                            </div>
                            {loadingOpiniones ? (
                                <p className="opiniones-loading">Cargando opiniones...</p>
                            ) : opinionesList.length === 0 ? (
                                <div className="opiniones-empty">
                                    No se han registrado opiniones o aclaraciones en esta votación.
                                </div>
                            ) : (
                                <div className="opiniones-list">
                                    {opinionesList.map((op) => (
                                        <div key={op.id} className="opinion-item-card">
                                            <div className="opinion-item-header">
                                                <span className="opinion-item-casa">🏠 Casa {op.casa || 'N/D'}</span>
                                                {op.residente && <span className="opinion-item-author">· {op.residente}</span>}
                                                <span className="opinion-item-date">{formatDateShort(op.votadoEn)}</span>
                                            </div>
                                            <p className="opinion-item-text">"{op.opinion}"</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="details-note">Si tienes más dudas sobre esta votación, por favor consulta con el administrador del condominio.</div>
                </div>
            )}
            <ConfirmDialog
                open={showDeleteConfirm}
                title="Eliminar votación"
                message="La votación y todos los votos asociados se eliminarán definitivamente. Esta acción no se puede deshacer."
                cancelLabel="Cancelar"
                confirmLabel="Eliminar"
                tone="danger"
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
            />
        </div>
    );
};

// Helper para convertir Firestore Timestamp o Date a string formateada
function formatDate(value) {
    if (!value) return '';
    // Firestore Timestamp tiene .toDate()
    const date = typeof value.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : new Date(value));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
}

function formatDateShort(value) {
    if (!value) return '';
    const date = typeof value?.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : new Date(value));
    if (isNaN(date.getTime())) return '';
    try {
        return new Intl.DateTimeFormat('es-MX', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    } catch (error) {
        return date.toLocaleString();
    }
}

function computeStatusBadge(estado) {
    const normalizado = String(estado || '').toLowerCase();
    if (normalizado === 'programada') {
        return { label: 'PRÓXIMA', className: 'badge-upcoming' };
    }
    if (normalizado === 'vencida') {
        return { label: 'VENCIDA', className: 'badge-expired' };
    }
    if (normalizado === 'activa') {
        return { label: 'ACTIVA', className: 'badge-active' };
    }
    return null;
}

export default VotacionCard;